import { useEffect, useState } from 'react'
import {
  ADDITIONAL_SALES_DETAIL_BACKGROUND,
  ADDITIONAL_SALES_HOME_BACKGROUND,
  ADDITIONAL_SALES_THEME_IDS,
  getAdditionalSalesThemeImageSrcs,
} from '../utils/additionalSalesAssets'

export const imageCache = {}

const getImageCacheKey = (src) => {
  if (!src) return src
  if (typeof window === 'undefined') return src

  return new URL(src, window.location.href).href
}

const getCachedImageEntry = (src) => imageCache[getImageCacheKey(src)] || imageCache[src]

const getLoadedImageSrcs = (srcs) => {
  const imgs = srcs.map((src) => {
    const cachedImage = getCachedImageEntry(src)
    return cachedImage?.loaded ? cachedImage.img : null
  })

  return imgs.every(Boolean) ? imgs.map((img) => img.src) : []
}

const waitForDecodedImage = (img) => {
  if (typeof img.decode !== 'function') return Promise.resolve()

  return img.decode().catch(() => undefined)
}

export const preloadImage = (src) => {
  if (!src) return Promise.resolve(null)

  const cacheKey = getImageCacheKey(src)
  const cachedImage = getCachedImageEntry(src)
  if (cachedImage?.loaded) return Promise.resolve(cachedImage.img)
  if (cachedImage?.promise) return cachedImage.promise

  const img = new Image()
  img.decoding = 'async'
  const cacheEntry = { img, src: cacheKey, loaded: false, promise: null }

  const promise = new Promise((resolve, reject) => {
    img.onload = () => {
      waitForDecodedImage(img).then(() => {
        Object.assign(cacheEntry, { img, src: img.src, loaded: true, promise })
        resolve(img)
      })
    }
    img.onerror = () => {
      delete imageCache[src]
      delete imageCache[cacheKey]
      reject(new Error(`Unable to preload image: ${src}`))
    }
  })

  cacheEntry.promise = promise
  imageCache[src] = cacheEntry
  imageCache[cacheKey] = cacheEntry
  img.src = cacheKey

  return promise
}

export const preloadImages = (srcs) => Promise.all(srcs.map((src) => preloadImage(src)))

export const usePreloadedBackgroundImage = (src) => {
  const [loadedSrc, setLoadedSrc] = useState(null)

  useEffect(() => {
    let isMounted = true

    if (!src) {
      setLoadedSrc(null)
      return undefined
    }

    preloadImage(src)
      .then((img) => {
        if (isMounted) setLoadedSrc(img.src)
      })
      .catch(() => {
        if (isMounted) setLoadedSrc(null)
      })

    return () => {
      isMounted = false
    }
  }, [src])

  return loadedSrc
}

export const usePreloadedImage = (themeId) => {
  const [srcs, setSrcs] = useState([])

  useEffect(() => {
    let isMounted = true

    if (!themeId) {
      setSrcs([])
      return undefined
    }

    const themeImageSrcs = getAdditionalSalesThemeImageSrcs(themeId)
    const loadedImageSrcs = getLoadedImageSrcs(themeImageSrcs)

    if (loadedImageSrcs.length) {
      setSrcs(loadedImageSrcs)
      return undefined
    }

    preloadImages(themeImageSrcs)
      .then((imgs) => {
        if (isMounted) setSrcs(imgs.map((img) => img.src))
      })
      .catch(() => {
        if (isMounted) setSrcs([])
      })

    return () => {
      isMounted = false
    }
  }, [themeId])

  return srcs
}

export const useAdditionalSalesAssetPreload = () => {
  const [homeBackgroundSrc, setHomeBackgroundSrc] = useState(null)
  const [detailBackgroundSrc, setDetailBackgroundSrc] = useState(null)
  const [homeLoaded, setHomeLoaded] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true
    const themeAssets = ADDITIONAL_SALES_THEME_IDS.flatMap(getAdditionalSalesThemeImageSrcs)

    preloadImage(ADDITIONAL_SALES_HOME_BACKGROUND)
      .then((img) => {
        if (!isMounted) return
        setHomeBackgroundSrc(img.src)
        setHomeLoaded(true)
      })
      .catch((err) => {
        if (!isMounted) return
        setError(err)
        setHomeLoaded(false)
      })

    preloadImage(ADDITIONAL_SALES_DETAIL_BACKGROUND)
      .then((img) => {
        if (isMounted) setDetailBackgroundSrc(img.src)
      })
      .catch((err) => {
        if (isMounted) setError(err)
      })

    preloadImages(themeAssets).catch((err) => {
      if (isMounted) setError(err)
    })

    return () => {
      isMounted = false
    }
  }, [])

  return { detailBackgroundSrc, homeBackgroundSrc, homeLoaded, error }
}
